<template>
    <redesign-base-chart
        v-if="hasData"
        class="w-full h-full"
        :widget="data"
        :option="chartOptions"
        :loading="isLoading"
    />
    <RedesignWidgetNoData
        v-else-if="!isLoading"
        :widget="data"
        class="h-full"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import RedesignWidgetNoData from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetNoData.vue'
import { WidgetExtended } from '@/types/dashboard'
import { ChartOptions } from '@/types/echart'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { AreaChartColors } from '@/enum/colors'

interface AreaChartApiData {
    series?: Array<{
        name: string
        data: Array<number | [number, number]>
        color?: string
        type?: string
    }>
    colors?: string[]
    xAxis?: {
        categories?: string[]
        type?: string
        allowDecimals?: boolean
        labels?: Record<string, unknown>
    }
    yAxis?: {
        title?: {
            text?: string
        }
        labels?: Record<string, unknown>
    }
    title?: {
        text?: string
    }
    legend?: {
        enabled?: boolean
    }
    chart?: {
        type?: string
    }
    tooltip?: {
        pointFormat?: string
        split?: boolean
    }
    plotOptions?: {
        area?: {
            pointStart?: number
            marker?: Record<string, unknown>
        }
    }
}

const props = defineProps<{
    data: WidgetExtended
}>()

// Get incoming and outgoing colors from widget layout
const incomingAreaColor = computed(() => {
    return props.data.WidgetLayout?.colors?.incoming ?? AreaChartColors.incoming
})

const outgoingAreaColor = computed(() => {
    return props.data.WidgetLayout?.colors?.outgoing ?? AreaChartColors.outgoing
})

// Fetch widget data with refresh interval
const { data: loadedData, isLoading } = useWidgetDataWithRefreshInterval<
    AreaChartApiData[],
    { data: WidgetExtended },
    'data'
>(props, 'data')

// Check if we have valid data
const hasData = computed(() => {
    if (!loadedData.value || !Array.isArray(loadedData.value) || loadedData.value.length === 0) {
        return false
    }

    const apiData = loadedData.value[0]
    return !!(apiData && apiData.series && apiData.series.length > 0)
})

// Transform API data to ECharts options
const chartOptions = computed((): ChartOptions => {
    if (!loadedData.value || !Array.isArray(loadedData.value) || loadedData.value.length === 0) {
        return {}
    }

    const apiData = loadedData.value[0]

    if (!apiData || !apiData.series) {
        return {}
    }

    // Get pointStart from plotOptions (default to 0)
    const pointStart = apiData.plotOptions?.area?.pointStart ?? 0

    // Map series data
    const series = apiData.series.map((serie, index) => {
        // Determine color: use serie.color if available, otherwise alternate between incoming/outgoing based on even/odd
        let color: string
        if (serie.color) {
            color = serie.color
        } else {
            // Alternate between incoming (even) and outgoing (odd) colors
            color = index % 2 === 0 ? incomingAreaColor.value : outgoingAreaColor.value
        }

        return {
            name: serie.name,
            type: 'line',
            data: serie.data,
            areaStyle: {
                opacity: 0.3
            },
            smooth: true,
            itemStyle: {
                color
            },
            lineStyle: {
                color,
                width: 2
            },
            emphasis: {
                focus: 'series'
            }
        }
    })

    // Build xAxis configuration
    let xAxisData: string[] | number[] | undefined
    if (apiData.xAxis?.categories) {
        // Use provided categories
        xAxisData = apiData.xAxis.categories
    } else if (apiData.series[0]?.data) {
        // Generate categories based on data length and pointStart
        const dataLength = apiData.series[0].data.length
        xAxisData = Array.from({ length: dataLength }, (_, i) => pointStart + i)
    }

    const xAxis = xAxisData
        ? {
            type: 'category',
            data: xAxisData,
            boundaryGap: false,
            axisLabel: {
                interval: 'auto',
            }
        }
        : {
            type: 'category',
            boundaryGap: false
        }

    // Build yAxis configuration
    const yAxis = {
        type: 'value',
        name: apiData.yAxis?.title?.text || ''
    }

    if (apiData.xAxis?.min) {
        xAxis.min = apiData.xAxis.min
    }
    if (apiData.xAxis?.max) {
        xAxis.max = apiData.xAxis.max
    }

    // Check if we should show percentage in tooltip (from API tooltip.pointFormat)
    const showPercentage = apiData.tooltip?.pointFormat?.includes('{point.percentage')

    // Base options
    return {
        tooltip: {
            trigger: 'axis',
            confine: false, // Allow tooltip to overflow widget boundaries
            appendToBody: true, // Append tooltip to body to prevent clipping
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            },
            // Custom formatter for percentage-based tooltips
            ...(showPercentage ? {
                formatter: (params: unknown) => {
                    if (!Array.isArray(params) || params.length === 0) return ''

                    const axisValue = params[0].axisValue
                    let total = 0

                    // Calculate total for percentage
                    params.forEach((param) => {
                        total += param.value || 0
                    })

                    let tooltip = `<div><strong>${axisValue}</strong></div>`

                    params.forEach((param) => {
                        const value = param.value || 0
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
                        const color = param.color

                        tooltip += `
                            <div style="margin: 4px 0;">
                                <span style="display:inline-block;width:10px;height:10px;background:${color};margin-right:5px;"></span>
                                <span>${param.seriesName}: <strong>${percentage}%</strong> (${value})</span>
                            </div>
                        `
                    })

                    return tooltip
                }
            } : {})
        },
        legend: {
            data: apiData.series.map(s => s.name),
            bottom: 10, // Add gap from bottom of widget
            left: 'center',
            itemGap: 10,
            type: apiData.series.length > 10 ? 'scroll' : 'plain',
            pageButtonItemGap: 5
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '15%', // Increased bottom padding
            top: '10%',
            containLabel: true
        },
        xAxis,
        yAxis,
        series
    }
})
</script>
